<template>
  <div class="space-y-4">
    <!-- Recording controls -->
    <div class="flex flex-col items-center gap-3">
      <button
        @click="toggleRecording"
        :disabled="state === 'processing'"
        :class="[
          'w-20 h-20 rounded-full flex items-center justify-center text-3xl transition-all',
          state === 'recording'
            ? 'bg-red-500 text-white animate-pulse shadow-lg shadow-red-200'
            : state === 'processing'
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
        ]"
      >
        {{ state === 'recording' ? '⏹' : state === 'processing' ? '⏳' : '🎤' }}
      </button>
      <p class="text-sm text-gray-500">
        {{ state === 'idle' ? 'Tap to record' : state === 'recording' ? 'Recording... tap to stop' : state === 'processing' ? 'Analyzing...' : '' }}
      </p>
    </div>

    <!-- Result -->
    <div v-if="state === 'result'" class="space-y-3">
      <!-- Tone coach: the learner's pitch curve vs the reference. Only
           rendered when both contours came back — a lone curve teaches nothing. -->
      <ToneContour
        v-if="userContour?.length > 1 && referenceContour?.length > 1"
        :reference="referenceContour"
        :user="userContour"
      />
      <div :class="['p-4 rounded-lg', bannerClass]">
        <p :class="['font-semibold', headingClass]">{{ headingText }}</p>
        <p v-if="toneMismatch" class="text-sm text-gray-600 mt-1">
          The sounds were right — just watch your tones.
        </p>
        <p v-if="recognizedText" class="text-sm text-gray-600 mt-1">
          We heard: <span class="font-medium">{{ recognizedText }}</span>
        </p>
        <p class="text-sm text-gray-600 mt-1">
          Correct: <span class="font-medium">{{ expectedText }}</span>
        </p>
      </div>
      <div class="flex gap-2 justify-center">
        <button
          @click="retry"
          class="text-sm text-emerald-600 hover:text-emerald-700 font-medium underline"
        >
          Try again
        </button>
        <button
          @click="acceptResult"
          class="bg-emerald-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-emerald-700"
        >
          Continue
        </button>
      </div>
    </div>

    <!-- Error / fallback -->
    <div v-if="errorMessage" class="text-sm text-red-500 text-center">
      {{ errorMessage }}
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onBeforeUnmount } from 'vue'
import { apiPostFile } from '../../api/client'
import { normalizeSouthernTones, stripDiacritics, levenshteinSimilarity } from '../../utils/similarity'
import { newAudioRecorder } from '../../utils/recording'
import ToneContour from './ToneContour.vue'

const props = defineProps({
  expectedText: { type: String, required: true },
  referenceAudioText: { type: String, default: null },
  // Pre-generated reference audio for this exercise; when present, the tone coach
  // overlays the learner's pitch curve on the reference after each attempt.
  referenceAudioUrl: { type: String, default: null },
  // Unit level, sent to the ASR endpoint so Level 1 speaking works without Pro.
  // Omitted (null) for Conversation practice, which stays subscription-gated.
  level: { type: Number, default: null },
})

const emit = defineEmits(['result'])

const state = ref('idle') // idle, recording, processing, result
// Held so the component can release the microphone if it is torn down
// mid-recording — skipping a speaking exercise does exactly that.
let activeStream = null
const userContour = ref(null)
const referenceContour = ref(null)
const recognizedText = ref('')
const resultCorrect = ref(false)
const resultClose = ref(false)
const toneMismatch = ref(false)
const errorMessage = ref('')

// Banner styling/copy across four outcomes: clean correct, correct-but-tones,
// close, and not-quite. Tone mismatches reuse the amber "needs attention" look.
const bannerClass = computed(() => {
  if (resultCorrect.value && !toneMismatch.value) return 'bg-green-50 border border-green-200'
  if (toneMismatch.value) return 'bg-amber-50 border border-amber-200'
  if (resultClose.value) return 'bg-blue-50 border border-blue-200'
  return 'bg-amber-50 border border-amber-200'
})
const headingClass = computed(() => {
  if (resultCorrect.value && !toneMismatch.value) return 'text-green-700'
  if (toneMismatch.value) return 'text-amber-700'
  if (resultClose.value) return 'text-blue-700'
  return 'text-amber-700'
})
const headingText = computed(() => {
  if (resultCorrect.value && !toneMismatch.value) return 'Great pronunciation!'
  if (toneMismatch.value) return 'Watch your tones!'
  if (resultClose.value) return 'Close! Try once more.'
  return 'Not quite right'
})

let mediaRecorder = null
let audioChunks = []

async function toggleRecording() {
  if (state.value === 'recording') {
    stopRecording()
  } else {
    await startRecording()
  }
}

async function startRecording() {
  errorMessage.value = ''
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
    activeStream = stream
    audioChunks = []
    mediaRecorder = newAudioRecorder(stream) // picks webm or mp4 per browser (iOS Safari has no webm)

    mediaRecorder.ondataavailable = (e) => {
      if (e.data.size > 0) audioChunks.push(e.data)
    }

    mediaRecorder.onstop = async () => {
      stream.getTracks().forEach(t => t.stop())
      const blob = new Blob(audioChunks, { type: mediaRecorder.mimeType || 'audio/webm' })
      await processAudio(blob)
    }

    mediaRecorder.start()
    state.value = 'recording'
  } catch (e) {
    errorMessage.value = 'Microphone access denied. Please allow microphone access to use speaking exercises.'
  }
}

// Skipping a speaking question unmounts this component while the recorder may
// still be running. Without this the microphone stayed open and `onstop` would
// later submit that clip against whatever question came next.
onBeforeUnmount(() => {
  if (mediaRecorder && mediaRecorder.state === 'recording') {
    mediaRecorder.onstop = null   // discard, don't grade
    try { mediaRecorder.stop() } catch (e) { /* already stopping */ }
  }
  if (activeStream) {
    activeStream.getTracks().forEach(t => t.stop())
    activeStream = null
  }
})

function stopRecording() {
  if (mediaRecorder && mediaRecorder.state === 'recording') {
    mediaRecorder.stop()
    state.value = 'processing'
  }
}

// Tone-coach contours, fetched fire-and-forget after the graded result is
// already on screen. Deliberately plain fetch (not the api client): a missing
// backend or gating response should silently skip the chart, never redirect
// or error mid-exercise.
async function fetchContours(blob) {
  if (!props.referenceAudioUrl) return
  const levelParam = props.level != null ? `&level=${props.level}` : ''
  try {
    const csrf = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
    const formData = new FormData()
    formData.append('audio', blob)
    const [userRes, refRes] = await Promise.all([
      fetch(`/api/v1/pitch/contour?${levelParam.slice(1)}`, {
        method: 'POST',
        headers: { 'Accept': 'application/json', 'X-CSRF-Token': csrf },
        body: formData,
      }),
      fetch(`/api/v1/pitch/reference?src=${encodeURIComponent(props.referenceAudioUrl)}${levelParam}`, {
        headers: { 'Accept': 'application/json' },
      }),
    ])
    if (!userRes.ok || !refRes.ok) return
    userContour.value = (await userRes.json()).points || null
    referenceContour.value = (await refRes.json()).points || null
  } catch (_e) {
    // Tone coach is a bonus layer — any failure just means no chart.
  }
}

async function processAudio(blob) {
  fetchContours(blob)
  try {
    const url = props.level != null
      ? `/api/v1/asr/transcribe?level=${props.level}`
      : '/api/v1/asr/transcribe'
    const data = await apiPostFile(url, blob)
    recognizedText.value = data.text || ''
    // Grade phonemes first (tones stripped), then check tones separately. PhoWhisper
    // is unreliable on Vietnamese tones, so a tone-only error shouldn't fail the
    // learner — accept it but nudge, mirroring the typed-answer lenient path.
    // The with-tones key folds the hỏi/ngã merger so a hook/tilde difference reads
    // as a clean match (same tone in Southern Vietnamese), not a tone mismatch.
    const simWithTones = levenshteinSimilarity(normalizeSouthernTones(recognizedText.value), normalizeSouthernTones(props.expectedText))
    const simNoTones = levenshteinSimilarity(stripDiacritics(recognizedText.value), stripDiacritics(props.expectedText))
    if (simWithTones >= 0.7) {
      resultCorrect.value = true
      toneMismatch.value = false
      resultClose.value = false
    } else if (simNoTones >= 0.7) {
      resultCorrect.value = true
      toneMismatch.value = true
      resultClose.value = false
    } else {
      resultCorrect.value = false
      toneMismatch.value = false
      resultClose.value = simNoTones >= 0.5
    }
    state.value = 'result'
  } catch (e) {
    // 422 = the recording itself couldn't be decoded (usually a too-short or
    // truncated capture) — the learner just needs to record again; anything
    // else means the ASR service is genuinely unavailable.
    errorMessage.value = e?.status === 422
      ? "We couldn't hear that recording — please try again."
      : 'Speech recognition service unavailable. Please try again.'
    state.value = 'idle'
  }
}

function retry() {
  state.value = 'idle'
  userContour.value = null
  referenceContour.value = null
  recognizedText.value = ''
  resultCorrect.value = false
  resultClose.value = false
  toneMismatch.value = false
  errorMessage.value = ''
}

function acceptResult() {
  emit('result', { correct: resultCorrect.value, recognizedText: recognizedText.value })
}
</script>
